import { GET, POST, PATCH, DELETE } from '@/shared/api/requests';
import type {
  Observation,
  ObservationSummary,
  ObservationStatusSummaryEntry,
  ObservationType,
  CreateObservationData,
  UpdateObservationData,
  ObservationFilters,
} from '../types';

const BASE = '/observations';

export const getObservations = async (
  projectId: number,
  filters: ObservationFilters = {},
): Promise<ObservationSummary[]> => {
  const params = new URLSearchParams({ project_id: String(projectId) });
  if (filters.status) params.set('status', filters.status);
  if (filters.priority) params.set('priority', filters.priority);
  if (filters.assignee) params.set('assignee', String(filters.assignee));
  if (filters.search) params.set('search', filters.search);
  if (filters.hotspot_id) params.set('hotspot_id', String(filters.hotspot_id));
  if (filters.photo_id) params.set('photo_id', String(filters.photo_id));
  if (filters.area_tag_id) params.set('area_tag_id', String(filters.area_tag_id));
  if (filters.area_tag_set_id) params.set('area_tag_set_id', String(filters.area_tag_set_id));
  const response = await GET(`${BASE}/items/?${params}`);
  const data = response.data;
  if (Array.isArray(data)) return data as ObservationSummary[];
  return (data as { results: ObservationSummary[] }).results;
};

export const getObservationById = async (
  id: string,
  projectId: number,
): Promise<Observation> => {
  const response = await GET(`${BASE}/items/${id}/?project_id=${projectId}`);
  return response.data as Observation;
};

export const createObservation = async (
  data: CreateObservationData,
): Promise<Observation> => {
  const response = await POST(`${BASE}/items/`, data);
  return response.data as Observation;
};

export const updateObservation = async (
  id: string,
  data: UpdateObservationData,
): Promise<Observation> => {
  const response = await PATCH(`${BASE}/items/${id}/`, data);
  return response.data as Observation;
};

export const deleteObservation = async (id: string): Promise<void> => {
  await DELETE(`${BASE}/items/${id}/`);
};

export const getObservationStatusSummary = async (
  projectId: number,
): Promise<ObservationStatusSummaryEntry[]> => {
  const response = await GET(`${BASE}/items/status-summary/?project_id=${projectId}`);
  const data = response.data;
  if (Array.isArray(data)) return data as ObservationStatusSummaryEntry[];
  return (data as { results: ObservationStatusSummaryEntry[] }).results ?? [];
};

export const getObservationTypes = async (): Promise<ObservationType[]> => {
  const response = await GET(`${BASE}/types/`);
  const data = response.data;
  if (Array.isArray(data)) return data as ObservationType[];
  return (data as { results: ObservationType[] }).results ?? [];
};

export const createObservationType = async (name: string): Promise<ObservationType> => {
  const response = await POST(`${BASE}/types/`, { name });
  return response.data as ObservationType;
};

export const uploadObservationAttachment = async (
  observationId: string,
  file: File,
): Promise<void> => {
  const formData = new FormData();
  formData.append('observation', observationId);
  formData.append('file', file);
  await POST(`${BASE}/attachments/`, formData);
};

export const linkObservationAttachmentPhoto = async (
  observationId: string,
  photoId: number,
): Promise<void> => {
  await POST(`${BASE}/attachments/`, { observation: observationId, photo: photoId });
};

export const deleteObservationAttachment = async (id: string): Promise<void> => {
  await DELETE(`${BASE}/attachments/${id}/`);
};
